import { useCallback, useEffect, useState } from "react";

import {
  createDbConnection,
  deleteDbConnection,
  deployAgent,
  listDbConnections,
  resetAgentToken,
  testDbConnection,
  uninstallAgent,
  updateDbConnection,
} from "@/api/dbConnections";
import type {
  AgentDeployResult,
  AgentTokenResetResult,
  AgentUninstallResult,
  DbConnection,
  DbConnectionCreate,
} from "@/types";

export function useDbConnections() {
  const [connections, setConnections] = useState<DbConnection[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await listDbConnections({ limit: 500, offset: 0 });
      setConnections(data.items);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load connections");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const create = async (connection: DbConnectionCreate): Promise<DbConnection | null> => {
    try {
      const created = await createDbConnection(connection);
      setConnections((prev) => [...prev, created]);
      return created;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to create connection");
      return null;
    }
  };

  const update = async (id: string, connection: DbConnectionCreate): Promise<DbConnection | null> => {
    try {
      const updated = await updateDbConnection(id, connection);
      setConnections((prev) => prev.map((c) => (c.id === id ? updated : c)));
      return updated;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to update connection");
      return null;
    }
  };

  const remove = async (id: string): Promise<boolean> => {
    try {
      await deleteDbConnection(id);
      setConnections((prev) => prev.filter((c) => c.id !== id));
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to delete connection");
      return false;
    }
  };

  const test = async (id: string): Promise<{ ok: boolean; message: string } | null> => {
    try {
      return await testDbConnection(id);
    } catch (err) {
      return { ok: false, message: err instanceof Error ? err.message : "Test failed" };
    }
  };

  const deploy = async (id: string): Promise<AgentDeployResult | null> => {
    try {
      const result = await deployAgent(id);
      await refresh();
      return result;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to deploy agent");
      return null;
    }
  };

  const resetToken = async (id: string): Promise<AgentTokenResetResult | null> => {
    try {
      const result = await resetAgentToken(id);
      await refresh();
      return result;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to reset agent token");
      return null;
    }
  };

  const uninstall = async (id: string): Promise<AgentUninstallResult | null> => {
    try {
      const result = await uninstallAgent(id);
      await refresh();
      return result;
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to uninstall agent");
      return null;
    }
  };

  return { connections, isLoading, error, refresh, create, update, remove, test, deploy, resetToken, uninstall };
}
